































































































































































































































































































import { computed, defineComponent, ref, toRefs } from '@nuxtjs/composition-api'
import { formFieldProps } from '~/constants/form'
import { useId } from '~/compositions/id'
import { mapBackendSearchableToOption } from '~/services/search/formatters'
import { range } from '~/utils/array'
import { faPlus, faTrash } from '@fortawesome/free-solid-svg-icons'
import { useClassifiedForm } from '~/compositions/form'
import { Ref } from '@vue/composition-api'
import CAutocompleteInput from '~/components/shared/configurable/form/autocomplete/CAutocompleteInput.vue'
import CCustomSelect from '~/components/shared/configurable/form/select/custom/CCustomSelect.vue'
import { useNamespacedStore } from '~/compositions/store'
import {
  CLASSIFIED_FORM_NS,
  FormState
} from '~/store/modules/shared/classifieds/form/state'
import { toCamelCase } from '~/utils/object'
import { toSnakeCase } from '~/utils/snake-case'
import {
  USER_AGENT_NS,
  UserAgentState
} from '~/store/modules/shared/userAgent/state'
import FieldError from '~/components/car/classifieds/form/fieldsets/fields/FieldError.vue'
import { capitalizeString } from '~/utils/string'
import Vue from 'vue'
import { useI18n } from '~/compositions/i18n'

interface MakeOrModelEntry {
  id?: number
  name?: string
}

interface ModelRowErrorField {
  key: string
  name: string
}
interface ModelRowError {
  fields: ModelRowErrorField[]
  errorMessage: string
}
interface ModelRow {
  model: MakeOrModelEntry
  yearFrom: number | null
  yearTo: number | null
  id: string
  error: ModelRowError | null
}
interface MakeRow {
  make: MakeOrModelEntry
  models: ModelRow[]
  id: string
}

interface FlattenedMakeModelRow {
  make: MakeOrModelEntry
  model: MakeOrModelEntry
  yearFrom: number | null
  yearTo: number | null
}

export default defineComponent({
  props: formFieldProps,
  components: { FieldError, CCustomSelect, CAutocompleteInput },
  setup(props) {
    const { specs, name } = toRefs(props)
    const { uuid } = useId()

    const { getters } = useNamespacedStore<FormState>(CLASSIFIED_FORM_NS)
    const showMakeLogos = computed(() => getters('isCarPartsForm'))
    const hasModelFieldError = ref<boolean>(false)
    const { t, tc } = useI18n()
    const { getters: userAgentGetters } = useNamespacedStore<UserAgentState>(
      USER_AGENT_NS
    )
    const isMobile = computed(() => userAgentGetters('isMobile'))

    function createModelRow() {
      return {
        model: { name: undefined, id: undefined },
        yearFrom: null,
        yearTo: null,
        id: uuid(),
        error: null
      }
    }

    function createMakeRow() {
      return {
        make: { name: undefined, id: undefined },
        models: [createModelRow()],
        id: uuid()
      }
    }

    const makeRows = ref<MakeRow[]>(isMobile.value ? [] : [createMakeRow()])

    const isEmpty = computed(() => {
      if (makeRows.value.length) {
        const firstValue = makeRows.value[0]
        return (
          makeRows.value.length === 1 &&
          !firstValue.make.name &&
          !firstValue.make.id
        )
      }
      return true
    })

    function onValue(v: Ref<any>) {
      if (v.value?.length) {
        const formatted = toCamelCase(v.value).reduce(
          (acc: MakeRow[], item: FlattenedMakeModelRow) => {
            const existing = acc.find(
              i => i.make.id === item.make.id || i.make.name === item.make.name
            )
            const model: ModelRow = {
              model: item.model,
              yearFrom: item.yearFrom,
              yearTo: item.yearTo,
              id: uuid()
            }
            if (existing) {
              existing.models.push(model)
            } else {
              const row: MakeRow = {
                make: item.make,
                models: [model],
                id: uuid()
              }
              acc.push(row)
            }
            return acc
          },
          []
        )
        makeRows.value = formatted
      }
    }

    function getErrorMessageForModelFields(fields: ModelRowErrorField[]) {
      return capitalizeString(
        tc(
          'the {fields} field is required | the {fields} fields are required',
          fields.length,
          {
            fields: '"' + fields.map(e => e.name).join('", "') + '"'
          }
        )
      )
    }

    function clientValidator(_error?: Ref<any>) {
      if (makeRows.value.length) {
        for (const [makeIndex, makeRow] of makeRows.value.entries()) {
          if (makeRow.make.id || makeRow.make.name) {
            // user has selected an id
            for (const modelRow of makeRow.models) {
              const errorFields: ModelRowErrorField[] = []
              if (
                (getModelsOptions(makeIndex).length === 0 &&
                  !modelRow.model.name) ||
                !modelRow.model.id
              ) {
                errorFields.push({ key: 'model', name: t('model').toString() })
              }
              if (!modelRow.yearFrom) {
                errorFields.push({
                  key: 'yearFrom',
                  name: `${t('registration')} ${t('from')
                    .toString()
                    .toLowerCase()}`
                })
              }
              if (!modelRow.yearTo) {
                errorFields.push({
                  key: 'yearTo',
                  name: `${t('registration')} ${t('to')
                    .toString()
                    .toLowerCase()}`
                })
              }

              if (errorFields.length) {
                hasModelFieldError.value = true
                const error = {
                  fields: errorFields,
                  errorMessage: getErrorMessageForModelFields(errorFields)
                }
                modelRow.error = error
              } else {
                hasModelFieldError.value = false
                modelRow.error = null
              }
            }
          }
        }
        return !hasModelFieldError.value
      }

      return true
    }

    function getModelRowState(
      modelRow: ModelRow,
      key: string,
      hasErrorMode: boolean = false
    ) {
      if (modelRow.error?.fields.find(f => f.key === key)) {
        return !!hasErrorMode
      }
      return hasErrorMode ? false : null
    }

    const { error, valueChanged } = useClassifiedForm(props, {
      onValue,
      clientValidator
    })

    const makesOptions = computed(() => {
      if (specs.value.makemodels.length) {
        return specs.value.makemodels.map(mapBackendSearchableToOption)
      }
      return []
    })
    const yearsOptions = computed(() =>
      range(specs.value.years.from, specs.value.years.to)
        .map(y => ({
          text: y,
          value: y
        }))
        .reverse()
    )

    function getModelsOptions(index: number) {
      const selectedMake = specs.value.makemodels.find(
        (make: any) => make.v === makeRows.value[index].make.id
      )
      if (selectedMake?.k?.length) {
        const makes = selectedMake.k.map(s => ({
          text: s.n,
          value: s.v
        }))
        if (makes.length) {
          const all = {
            text: t('all'),
            value: 'Όλα'
          }
          return [all, ...makes]
        }
      }
      return []
    }

    function handleMakeChange(index: number, value: MakeOrModelEntry) {
      // make.id is set by v-model but we also need to set name
      const selectedMake = specs.value.makemodels.find(
        (make: any) => make.v === value
      )
      if (selectedMake) {
        makeRows.value[index].make.name = selectedMake.n
      }
      makeRows.value[index].models = [createModelRow()]
      emitValues()
    }

    function handleModelFieldChange(
      makeIndex: number,
      modelIndex: number,
      key: string,
      value: number
    ) {
      const selectedModel = makeRows.value[makeIndex].models[modelIndex]

      if (selectedModel.error?.fields.find(f => f.key === key) && value) {
        // we had error but not its fixed
        const fields = selectedModel.error.fields.filter(f => f.key !== key)

        const error = fields?.length
          ? {
              fields,
              errorMessage: getErrorMessageForModelFields(fields)
            }
          : null
        Vue.set(makeRows.value[makeIndex].models[modelIndex], 'error', error)
      }

      // model.id is set by v-model but we also need to set name
      if (key === 'model') {
        const options = getModelsOptions(makeIndex)
        const selectedOption = options.find(o => o.value === value)
        if (selectedOption) {
          selectedModel.model.name = selectedOption.text
        }
      }

      emitValues()
    }

    function addMakeRow() {
      makeRows.value.push(createMakeRow())
      emitValues()
    }

    function addModelRow(makeIndex: number) {
      makeRows.value[makeIndex].models.push(createModelRow())
      emitValues()
    }
    function removeForMakeModelRow(makeIndex: number, modelIndex: number) {
      makeRows.value[makeIndex].models.splice(modelIndex, 1)
      if (makeRows.value[makeIndex].models.length === 0) {
        makeRows.value.splice(makeIndex, 1)
      }
      emitValues()
    }

    function emitValues() {
      let flattenedMakeModelRows = []
      if (!isEmpty.value) {
        flattenedMakeModelRows = makeRows.value.reduce<FlattenedMakeModelRow[]>(
          (acc, row) => {
            row.models.forEach(model => {
              acc.push({
                make: row.make,
                model: model.model,
                yearFrom: model.yearFrom,
                yearTo: model.yearTo
              })
            })
            return acc
          },
          []
        )
      }

      valueChanged(name.value, toSnakeCase(flattenedMakeModelRows))
    }

    return {
      makesOptions,
      makeRows,
      getModelsOptions,
      handleMakeChange,
      addMakeRow,
      removeForMakeModelRow,
      emitValues,
      addModelRow,
      yearsOptions,
      faTrash,
      faPlus,
      showMakeLogos,
      isMobile,
      isEmpty,
      error,
      clientValidator,
      getModelRowState,
      hasModelFieldError,
      handleModelFieldChange,
      getErrorMessageForModelFields
    }
  }
})
